<template>
  <div>
    <div class="d-flex align-items-center justify-content-between mb-4">
      <div class="d-flex align-items-center">
        <CButton
          color="link"
          class="p-0 mr-3"
          @click="$router.push({ name: 'customer_success.search' })"
        >
          <CIcon name="cil-arrow-left" size="lg"/>
        </CButton>
        <h1 class="main-header mb-0">
          <translate translate-context="customer_success.user_detail">User</translate>
          #{{ $route.params.id }}
        </h1>
      </div>
      <CButton
        v-if="canImpersonate && user"
        color="warning"
        @click="onImpersonate"
      >
        <CIcon name="cil-user" size="sm" class="mr-1"/>
        <translate translate-context="customer_success.user_detail">Impersonate</translate>
      </CButton>
    </div>

    <div v-if="user">
      <!-- First Row: User Info & Account Settings -->
      <CRow class="mb-4">
        <!-- User Info (read-only) -->
        <CCol lg="5" md="6" class="mb-3">
          <CCard class="h-100">
            <CCardHeader>
              <CIcon name="cil-user" size="sm" class="mr-2"/>
              <translate translate-context="customer_success.user_detail">User Info</translate>
            </CCardHeader>
            <CCardBody>
              <div class="detail-row">
                <span class="detail-label">ID</span>
                <span class="detail-value">{{ user.id }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Email</span>
                <span class="detail-value">{{ user.email }}</span>
              </div>
              <div v-if="user.full_name" class="detail-row">
                <span class="detail-label">
                  <translate translate-context="customer_success.user_detail">Name</translate>
                </span>
                <span class="detail-value">{{ user.full_name }}</span>
              </div>
              <div v-if="user.date_joined" class="detail-row">
                <span class="detail-label">
                  <translate translate-context="customer_success.user_detail">Joined</translate>
                </span>
                <span class="detail-value">{{ formatShortDate(user.date_joined) }}</span>
              </div>
              <div v-if="user.last_login" class="detail-row">
                <span class="detail-label">
                  <translate translate-context="customer_success.user_detail">Last Login</translate>
                </span>
                <span class="detail-value">{{ formatShortDate(user.last_login) }}</span>
              </div>
            </CCardBody>
          </CCard>
        </CCol>

        <!-- Account Settings (with toggles and actions) -->
        <CCol lg="7" md="6" class="mb-3">
          <CCard class="h-100">
            <CCardHeader>
              <CIcon name="cil-settings" size="sm" class="mr-2"/>
              <translate translate-context="customer_success.user_detail">Account Settings</translate>
            </CCardHeader>
            <CCardBody>
              <!-- Status Toggle -->
              <div class="setting-row">
                <div class="setting-info">
                  <span class="setting-label">Status</span>
                  <span class="setting-description text-muted small">
                    <translate translate-context="customer_success.user_detail">User can access their account</translate>
                  </span>
                </div>
                <div class="setting-control">
                  <CSwitch
                    color="success"
                    shape="pill"
                    :checked="user.is_active"
                    @update:checked="onToggleStatus"
                  />
                  <span class="setting-status" :class="user.is_active ? 'text-success' : 'text-danger'">
                    {{ user.is_active ? 'Active' : 'Inactive' }}
                  </span>
                </div>
              </div>

              <!-- Dashboard Toggle -->
              <div class="setting-row">
                <div class="setting-info">
                  <span class="setting-label">Dashboard</span>
                  <span class="setting-description text-muted small">
                    <translate translate-context="customer_success.user_detail">Access to Pica Dashboard</translate>
                  </span>
                </div>
                <div class="setting-control">
                  <CSwitch
                    color="info"
                    shape="pill"
                    :checked="user.is_pica_dashboard_user"
                    @update:checked="onToggleDashboard"
                  />
                  <span class="setting-status" :class="user.is_pica_dashboard_user ? 'text-info' : 'text-muted'">
                    {{ user.is_pica_dashboard_user ? 'Enabled' : 'Disabled' }}
                  </span>
                </div>
              </div>

              <!-- Real Email Toggle -->
              <div class="setting-row">
                <div class="setting-info">
                  <span class="setting-label">
                    <translate translate-context="customer_success.user_detail">Real Email</translate>
                  </span>
                  <span class="setting-description text-muted small">
                    {{ isRealEmail
                      ? $pgettext('customer_success.user_detail', 'User will receive login token via email')
                      : $pgettext('customer_success.user_detail', 'User will login with password only')
                    }}
                  </span>
                </div>
                <div class="setting-control">
                  <CSwitch
                    color="success"
                    shape="pill"
                    :checked="isRealEmail"
                    @update:checked="onToggleRealEmail"
                  />
                  <span class="setting-status" :class="isRealEmail ? 'text-success' : 'text-warning'">
                    {{ isRealEmail ? 'Email vera' : 'Email virtuale' }}
                  </span>
                </div>
              </div>

              <!-- Password -->
              <div class="setting-row">
                <div class="setting-info">
                  <span class="setting-label">Password</span>
                  <span class="setting-description text-muted small">
                    <translate translate-context="customer_success.user_detail">Set a new password for the user</translate>
                  </span>
                </div>
                <div class="setting-control setting-control-input">
                  <CInput
                    v-model="reset.password"
                    type="text"
                    size="sm"
                    placeholder="New password..."
                    class="password-input"
                  />
                  <CButton
                    color="warning"
                    size="sm"
                    :disabled="!reset.password"
                    @click="onChangePassword"
                  >
                    <CIcon name="cil-lock-locked" size="sm" class="mr-1"/>
                    <translate translate-context="customer_success.user_detail">Set</translate>
                  </CButton>
                </div>
              </div>

              <!-- Email Change (if needed) -->
              <div class="setting-row">
                <div class="setting-info">
                  <span class="setting-label">
                    <translate translate-context="customer_success.user_detail">Change Email</translate>
                  </span>
                  <span class="setting-description text-muted small">
                    <translate translate-context="customer_success.user_detail">Update the user's email address</translate>
                  </span>
                </div>
                <div class="setting-control setting-control-input">
                  <CInput
                    v-model="newEmail"
                    type="email"
                    size="sm"
                    :placeholder="user.email"
                    class="email-input"
                  />
                  <CButton
                    color="primary"
                    size="sm"
                    :disabled="!newEmail || newEmail === user.email"
                    @click="onChangeEmail"
                  >
                    <CIcon name="cil-envelope-closed" size="sm" class="mr-1"/>
                    <translate translate-context="customer_success.user_detail">Set</translate>
                  </CButton>
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <!-- Second Row: Transactions & Checkins -->
      <CRow>
        <!-- Transactions Column -->
        <CCol lg="6" class="mb-3">
          <CCard class="h-100">
            <CCardHeader class="d-flex align-items-center">
              <CIcon name="cil-history" size="sm" class="mr-2 text-primary"/>
              <translate translate-context="customer_success.user_detail">Transactions</translate>
              <CBadge v-if="totalTransactions > 0" color="primary" class="ml-2">
                {{ totalTransactions }}
              </CBadge>
            </CCardHeader>
            <CCardBody class="p-0">
              <div v-if="loadingTransactions" class="text-center py-4">
                <CSpinner size="sm"/>
              </div>

              <div v-else-if="transactions.length === 0" class="text-muted text-center py-4">
                <CIcon name="cil-ban" size="xl" class="mb-2 d-block mx-auto"/>
                <translate translate-context="customer_success.user_detail">No transactions found</translate>
              </div>

              <div v-else class="items-list">
                <router-link
                  v-for="tx in transactions"
                  :key="tx.id"
                  :to="getTransactionRoute(tx)"
                  target="_blank"
                  class="list-item"
                >
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <span class="text-primary">{{ getTransactionLabel(tx) }}</span>
                    </div>
                    <div class="text-right">
                      <strong :class="getAmountClass(tx.total_amount)">{{ formatAmount(tx.total_amount) }}</strong>
                      <span class="text-muted small ml-2">{{ formatShortDate(tx.effective_date) }}</span>
                    </div>
                  </div>
                </router-link>
              </div>
            </CCardBody>
          </CCard>
        </CCol>

        <!-- Checkins Column -->
        <CCol lg="6" class="mb-3">
          <CCard class="h-100">
            <CCardHeader class="d-flex align-items-center">
              <CIcon name="cil-location-pin" size="sm" class="mr-2 text-success"/>
              <translate translate-context="customer_success.user_detail">Checkins</translate>
              <CBadge v-if="totalCheckins > 0" color="success" class="ml-2">
                {{ totalCheckins }}
              </CBadge>
            </CCardHeader>
            <CCardBody class="p-0">
              <div v-if="loadingCheckins" class="text-center py-4">
                <CSpinner size="sm"/>
              </div>

              <div v-else-if="checkins.length === 0" class="text-muted text-center py-4">
                <CIcon name="cil-ban" size="xl" class="mb-2 d-block mx-auto"/>
                <translate translate-context="customer_success.user_detail">No checkins found</translate>
              </div>

              <div v-else class="items-list">
                <router-link
                  v-for="checkin in checkins"
                  :key="checkin.id"
                  :to="getCheckinRoute(checkin)"
                  target="_blank"
                  class="list-item"
                >
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <span class="text-primary">{{ checkin.event_name || '-' }}</span>
                      <span v-if="checkin.picacode" class="text-muted small ml-2">{{ checkin.picacode }}</span>
                    </div>
                    <div class="text-right text-muted small">
                      {{ formatShortDate(checkin.checkin_date) }}
                    </div>
                  </div>
                </router-link>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <!-- Third Row: Collaborators -->
      <CRow v-if="canViewCollaborators">
        <CCol>
          <CCard>
            <CCardHeader class="d-flex align-items-center">
              <CIcon name="cil-people" size="sm" class="mr-2 text-info"/>
              <translate translate-context="customer_success.user_detail">Collaborations</translate>
              <CBadge v-if="collaborators.length > 0" color="info" class="ml-2">{{ collaborators.length }}</CBadge>
            </CCardHeader>
            <CCardBody class="p-0">
              <div v-if="loadingCollaborators" class="text-center py-4">
                <CSpinner size="sm"/>
              </div>

              <div v-else-if="collaborators.length === 0" class="text-muted text-center py-4">
                <CIcon name="cil-ban" size="xl" class="mb-2 d-block mx-auto"/>
                <translate translate-context="customer_success.user_detail">No collaborations found</translate>
              </div>

              <table v-else class="table table-sm table-borderless mb-0">
                <thead>
                  <tr>
                    <th><translate translate-context="customer_success.user_detail">Scope</translate></th>
                    <th><translate translate-context="customer_success.user_detail">Name</translate></th>
                    <th><translate translate-context="customer_success.user_detail">Roles</translate></th>
                    <th><translate translate-context="customer_success.user_detail">Status</translate></th>
                    <th><translate translate-context="customer_success.user_detail">Since</translate></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="collab in collaborators" :key="collab.id">
                    <td class="text-muted small">
                      {{ formatScopeType(collab.scope_type) }}
                    </td>
                    <td>
                      <router-link
                        v-if="collab.contract_manager_id"
                        :to="{ name: 'contract_manager.detail', params: { id: collab.contract_manager_id } }"
                        target="_blank"
                      >
                        {{ collab.scope_name }}
                      </router-link>
                      <router-link
                        v-else-if="collab.pica_service_id"
                        :to="{ name: 'eventWizard.date_and_name', params: { picaServiceId: collab.pica_service_id } }"
                        target="_blank"
                      >
                        {{ collab.scope_name }}
                      </router-link>
                      <span v-else>{{ collab.scope_name }}</span>
                    </td>
                    <td>
                      <span v-for="(role, idx) in collab.roles" :key="role.role_name">
                        {{ role.role_name }}<span v-if="role.expires_at" class="text-warning"> *</span><span v-if="idx < collab.roles.length - 1">, </span>
                      </span>
                    </td>
                    <td>
                      <span :class="getStatusClass(collab.status)">{{ collab.status }}</span>
                    </td>
                    <td class="text-muted small">
                      {{ formatShortDate(collab.created_at) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </div>

    <!-- Loading State -->
    <div v-else class="text-center py-5">
      <CSpinner/>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { getDetailRouteParams, TRANSACTION_TYPE_LABELS } from '@/domain/accounting/constants'

export default {
  name: 'UserDetail',
  data() {
    return {
      reset: {
        password: ''
      },
      newEmail: '',
      loadingTransactions: false,
      transactions: [],
      totalTransactions: 0,
      loadingCheckins: false,
      checkins: [],
      totalCheckins: 0,
      loadingCollaborators: false,
      collaborators: []
    }
  },
  computed: {
    ...mapGetters('customerSuccess', ['user']),
    ...mapGetters('login', ['hasPerm']),
    isRealEmail() {
      return !this.user?.is_virtual
    },
    canImpersonate() {
      return this.hasPerm('dashboard.impersonate')
    },
    canViewCollaborators() {
      return this.hasPerm('admin.users.collaborators')
    }
  },
  async mounted() {
    await this.fetchUser(this.$route.params.id)
    await this.fetchConfig({ type: 'admin' })
    this.loadTransactionsAndCheckins()
    this.loadCollaborators()
  },
  methods: {
    ...mapActions('customerSuccess', [
      'fetchUser',
      'fetchCollaborators',
      'reactivate',
      'deactivate',
      'changePassword',
      'enableDashboard',
      'disableDashboard',
      'setVirtualUser',
      'unsetVirtualUser',
      'changeAccountEmail'
    ]),
    ...mapActions('accounting', ['setScope', 'fetchUserTransactions', 'fetchUserCheckins']),
    ...mapActions('impersonate', ['impersonate']),
    ...mapActions('login', ['fetchConfig']),

    async loadTransactionsAndCheckins() {
      // CustomerSuccess uses global scope for accounting data
      await this.setScope({ type: 'global' })
      this.loadTransactions()
      this.loadCheckins()
    },

    async loadTransactions() {
      const userId = this.$route.params.id
      if (!userId) return

      this.loadingTransactions = true
      try {
        const result = await this.fetchUserTransactions({
          userId,
          limit: 1000
        })
        this.transactions = result.results || result || []
        this.totalTransactions = result.total || this.transactions.length
      } catch (error) {
        this.transactions = []
        this.totalTransactions = 0
      } finally {
        this.loadingTransactions = false
      }
    },

    async loadCheckins() {
      const userId = this.$route.params.id
      if (!userId) return

      this.loadingCheckins = true
      try {
        const result = await this.fetchUserCheckins({
          userId,
          limit: 1000
        })
        this.checkins = result.results || result || []
        this.totalCheckins = result.total || this.checkins.length
      } catch (error) {
        this.checkins = []
        this.totalCheckins = 0
      } finally {
        this.loadingCheckins = false
      }
    },

    async loadCollaborators() {
      if (!this.canViewCollaborators) return

      const userId = this.$route.params.id
      if (!userId) return

      this.loadingCollaborators = true
      try {
        const result = await this.fetchCollaborators(userId)
        this.collaborators = result || []
      } catch (error) {
        this.collaborators = []
      } finally {
        this.loadingCollaborators = false
      }
    },

    getTransactionRoute(tx) {
      return getDetailRouteParams(tx.transaction_type, tx.source_id)
    },

    getTransactionLabel(tx) {
      const typeLabel = TRANSACTION_TYPE_LABELS[tx.transaction_type] || tx.transaction_type
      return `${typeLabel} #${tx.source_id}`
    },

    getCheckinRoute(checkin) {
      if (checkin.pica_service_id) {
        return {
          name: 'checkin.detail',
          params: {
            picaServiceId: checkin.pica_service_id,
            id: checkin.id
          }
        }
      }
      return {}
    },

    getAmountClass(amount) {
      const num = parseFloat(amount)
      if (num > 0) return 'text-success'
      if (num < 0) return 'text-danger'
      return ''
    },

    formatAmount(amount) {
      const num = parseFloat(amount) || 0
      return num.toFixed(2) + ' EUR'
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString()
    },

    formatShortDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString()
    },

    onChangePassword() {
      if (this.reset.password.length < 3) {
        return this.notifyError(this.$pgettext('customer_success.user_detail', 'Password must be at least 3 characters'))
      }
      this.changePassword(this.reset.password)
      this.reset.password = ''
      this.notifySuccess(this.$pgettext('customer_success.user_detail', 'Password changed'))
    },

    onToggleStatus(isActive) {
      if (isActive) {
        this.reactivate()
        this.notifySuccess(this.$pgettext('customer_success.user_detail', 'User activated'))
      } else {
        this.deactivate()
        this.notifySuccess(this.$pgettext('customer_success.user_detail', 'User deactivated'))
      }
    },

    onToggleDashboard(isEnabled) {
      if (isEnabled) {
        this.enableDashboard()
        this.notifySuccess(this.$pgettext('customer_success.user_detail', 'Dashboard access enabled'))
      } else {
        this.disableDashboard()
        this.notifySuccess(this.$pgettext('customer_success.user_detail', 'Dashboard access disabled'))
      }
    },

    async onToggleRealEmail(isRealEmail) {
      try {
        if (isRealEmail) {
          // Real email = unset virtual
          await this.unsetVirtualUser(this.user.id)
          this.notifySuccess(this.$pgettext('customer_success.user_detail', 'Email set as real'))
        } else {
          // Virtual email = set virtual
          await this.setVirtualUser(this.user.id)
          this.notifySuccess(this.$pgettext('customer_success.user_detail', 'Email set as virtual'))
        }
      } catch (error) {
        this.notifyError(this.$pgettext('customer_success.user_detail', 'Failed to update email type'))
      }
    },

    async onChangeEmail() {
      if (!this.newEmail || this.newEmail === this.user.email) return

      try {
        await this.changeAccountEmail({
          userId: this.user.id,
          email: this.newEmail
        })
        this.newEmail = ''
        this.notifySuccess(this.$pgettext('customer_success.user_detail', 'Email changed'))
        await this.fetchUser(this.$route.params.id)
      } catch (error) {
        this.notifyError(this.$pgettext('customer_success.user_detail', 'Failed to change email'))
      }
    },

    async onImpersonate() {
      await this.impersonate(this.user)
      this.$router.push({ name: 'home' })
    },

    formatScopeType(scopeType) {
      const labels = {
        'CONTRACT_MANAGER': 'Contract Manager',
        'PICA_SERVICE': 'Pica Service'
      }
      return labels[scopeType] || scopeType
    },

    getStatusClass(status) {
      const classes = {
        'ACCEPTED': 'text-success',
        'PENDING': 'text-warning',
        'REJECTED': 'text-danger',
        'REMOVED': 'text-muted'
      }
      return classes[status] || 'text-muted'
    }
  }
}
</script>

<style scoped>
.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #ebedef;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  color: #768192;
  font-size: 0.875rem;
}

.detail-value {
  font-weight: 500;
  color: #3c4b64;
}

/* Setting rows for Account Settings card */
.setting-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #ebedef;
}

.setting-row:last-child {
  border-bottom: none;
}

.setting-info {
  display: flex;
  flex-direction: column;
}

.setting-label {
  font-weight: 500;
  color: #3c4b64;
  margin-bottom: 0.125rem;
}

.setting-description {
  font-size: 0.75rem;
}

.setting-control {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.setting-control-input {
  display: flex;
  gap: 0.5rem;
}

.setting-status {
  font-size: 0.75rem;
  font-weight: 500;
  min-width: 60px;
  text-align: right;
}

.password-input,
.email-input {
  width: 160px;
}

.items-list {
  max-height: 500px;
  overflow-y: auto;
}

.list-item {
  display: block;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #ebedef;
  text-decoration: none;
  color: inherit;
  transition: background-color 0.15s ease;
}

.list-item:last-child {
  border-bottom: none;
}

.list-item:hover {
  background-color: #f8f9fa;
  text-decoration: none;
}

.gap-2 {
  gap: 0.5rem;
}

.h-100 {
  height: 100%;
}

</style>
